import React, { useState } from 'react';


import Icon from '../../../components/AppIcon';

const ContextStep = ({ formData, updateFormData, validationErrors }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const relationships = [
    { id: 'family', label: 'Family', icon: 'Users' },
    { id: 'friend', label: 'Friend', icon: 'Heart' },
    { id: 'colleague', label: 'Colleague', icon: 'Briefcase' },
    { id: 'partner', label: 'Partner', icon: 'Heart' },
    { id: 'acquaintance', label: 'Acquaintance', icon: 'User' },
    { id: 'mentor', label: 'Mentor', icon: 'Star' },
    { id: 'client', label: 'Client', icon: 'Building' },
    { id: 'neighbor', label: 'Neighbor', icon: 'Home' }
  ];

  const budgetRanges = [
    { id: 'under_25', label: 'Under $25', icon: 'DollarSign' },
    { id: '25_50', label: '$25 - $50', icon: 'DollarSign' },
    { id: '50_100', label: '$50 - $100', icon: 'DollarSign' },
    { id: '100_200', label: '$100 - $200', icon: 'DollarSign' },
    { id: '200_500', label: '$200 - $500', icon: 'DollarSign' },
    { id: 'over_500', label: 'Over $500', icon: 'DollarSign' },
    { id: 'no_limit', label: 'No specific limit', icon: 'Infinity' }
  ];

  const significanceLevels = [
    { id: 'casual', label: 'Casual', description: 'A thoughtful gesture', icon: 'Smile' },
    { id: 'important', label: 'Important', description: 'A meaningful moment', icon: 'Heart' },
    { id: 'milestone', label: 'Milestone', description: 'A major life event', icon: 'Award' },
    { id: 'once_in_lifetime', label: 'Once in a Lifetime', description: 'An extraordinary occasion', icon: 'Star' }
  ];

  const handleReminderDaysChange = (days) => {
    updateFormData('reminderSettings', {
      ...formData.reminderSettings,
      days: days
    });
  };

  const handleReminderTimeChange = (time) => {
    updateFormData('reminderSettings', {
      ...formData.reminderSettings,
      times: [time]
    });
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      updateFormData('tags', [...formData.tags, tag]);
    }
  };

  const removeTag = (tagToRemove) => {
    updateFormData('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  const suggestedTags = [
    'Surprise', 'Practical', 'Luxury', 'Handmade', 'Experiential', 
    'Sentimental', 'Funny', 'Personalized', 'Eco-friendly', 'Tech'
  ];

  return (
    <div className="space-y-8">
      {/* Step Title */}
      <div className="text-center">
        <h2 className="font-heading font-semibold text-2xl text-accent-800 mb-2">
          Context & Preferences
        </h2>
        <p className="text-text-secondary">
          Help us understand the relationship and what makes this occasion special
        </p>
      </div>

      {/* Relationship */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          Your relationship to the recipient *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {relationships.map((relationship) => (
            <button
              key={relationship.id}
              onClick={() => updateFormData('relationship', relationship.id)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                formData.relationship === relationship.id
                  ? 'border-primary bg-primary-50 text-primary' :'border-accent-200 hover:border-accent-300 text-accent-600'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  formData.relationship === relationship.id
                    ? 'bg-primary text-white' :'bg-accent-100 text-accent-600'
                }`}>
                  <Icon name={relationship.icon} size={18} />
                </div>
                <span className="text-sm font-medium">{relationship.label}</span>
              </div>
            </button>
          ))}
        </div>
        {validationErrors.relationship && (
          <p className="text-red-500 text-sm mt-2">{validationErrors.relationship}</p>
        )}
      </div>

      {/* Significance Level */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          How significant is this occasion?
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {significanceLevels.map((level) => (
            <button
              key={level.id}
              onClick={() => updateFormData('significance', level.id)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md text-left ${
                formData.significance === level.id
                  ? 'border-primary bg-primary-50' :'border-accent-200 hover:border-accent-300'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  formData.significance === level.id
                    ? 'bg-primary text-white' :'bg-accent-100 text-accent-600'
                }`}>
                  <Icon name={level.icon} size={18} />
                </div>
                <div>
                  <p className="font-medium text-accent-800">{level.label}</p>
                  <p className="text-sm text-text-secondary">{level.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Budget Range */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          Budget considerations
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {budgetRanges.map((budget) => (
            <button
              key={budget.id}
              onClick={() => updateFormData('budgetRange', budget.id)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                formData.budgetRange === budget.id
                  ? 'border-primary bg-primary-50 text-primary' :'border-accent-200 hover:border-accent-300 text-accent-600'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  formData.budgetRange === budget.id
                    ? 'bg-primary text-white' :'bg-accent-100 text-accent-600'
                }`}>
                  <Icon name={budget.icon} size={18} />
                </div>
                <span className="text-sm font-medium text-center">{budget.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          Special notes or preferences
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => updateFormData('notes', e.target.value)}
          placeholder="Any specific preferences, interests, or things to avoid? The more details, the better our recommendations!"
          rows={4}
          className="w-full px-4 py-3 rounded-xl border-2 border-accent-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-200 resize-none"
        />
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-accent-800 mb-3">
          Gift style preferences (optional)
        </label>
        <div className="space-y-3">
          {/* Selected Tags */}
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                >
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Suggested Tags */}
          <div className="flex flex-wrap gap-2">
            {suggestedTags.filter(tag => !formData.tags.includes(tag)).map((tag) => (
              <button
                key={tag}
                onClick={() => addTag(tag)}
                className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm hover:bg-accent-200 transition-colors duration-200"
              >
                + {tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Advanced Options */}
      <div>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center space-x-2 text-accent-600 hover:text-accent-800 transition-colors duration-200"
        >
          <Icon name={showAdvanced ? "ChevronUp" : "ChevronDown"} size={18} />
          <span className="font-medium">Advanced Options</span>
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-6 p-4 bg-accent-50 rounded-xl border border-accent-200">
            {/* Reminder Settings */}
            <div>
              <label className="block text-sm font-medium text-accent-800 mb-3">
                Reminder Settings
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="enableReminders"
                    checked={formData.reminderSettings.enabled}
                    onChange={(e) => updateFormData('reminderSettings', {
                      ...formData.reminderSettings,
                      enabled: e.target.checked
                    })}
                    className="rounded border-accent-300 text-primary focus:ring-primary-200"
                  />
                  <label htmlFor="enableReminders" className="text-sm text-accent-800">
                    Enable reminders
                  </label>
                </div>

                {formData.reminderSettings.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-accent-600 mb-2">
                        Remind me (days before)
                      </label>
                      <select
                        value={formData.reminderSettings.days.join(',')}
                        onChange={(e) => handleReminderDaysChange(e.target.value.split(',').map(Number))}
                        className="w-full px-3 py-2 rounded-lg border border-accent-200 focus:border-primary focus:outline-none text-sm"
                      >
                        <option value="7,3,1">7, 3, 1 days before</option>
                        <option value="14,7,1">14, 7, 1 days before</option>
                        <option value="30,7,1">30, 7, 1 days before</option>
                        <option value="1">1 day before</option>
                        <option value="7">1 week before</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-accent-600 mb-2">
                        Reminder time
                      </label>
                      <input
                        type="time"
                        value={formData.reminderSettings.times[0]}
                        onChange={(e) => handleReminderTimeChange(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-accent-200 focus:border-primary focus:outline-none text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recurring Occasion */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={formData.isRecurring}
                  onChange={(e) => updateFormData('isRecurring', e.target.checked)}
                  className="rounded border-accent-300 text-primary focus:ring-primary-200"
                />
                <label htmlFor="isRecurring" className="text-sm font-medium text-accent-800">
                  This is a recurring occasion
                </label>
              </div>

              {formData.isRecurring && (
                <div>
                  <label className="block text-xs text-accent-600 mb-2">
                    Repeat pattern
                  </label>
                  <select
                    value={formData.recurringPattern}
                    onChange={(e) => updateFormData('recurringPattern', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-accent-200 focus:border-primary focus:outline-none text-sm"
                  >
                    <option value="yearly">Every year</option>
                    <option value="monthly">Every month</option>
                    <option value="weekly">Every week</option>
                  </select>
                </div>
              )}
            </div>

            {/* Calendar Sync */}
            <div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="syncCalendar"
                  checked={formData.syncWithCalendar}
                  onChange={(e) => updateFormData('syncWithCalendar', e.target.checked)}
                  className="rounded border-accent-300 text-primary focus:ring-primary-200"
                />
                <label htmlFor="syncCalendar" className="text-sm text-accent-800">
                  Sync with calendar apps
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextStep;